import {
	ProductSearchResultState as ProductSearchResultType,
	ProductSearchParams
} from 'coreStore/data/productSearchResult/interface';
import ProductModel from 'coreClasses/product/ProductModel';
import {EEventSource, EEventType} from 'coreClasses/events/IEvents';
import {getConfig, isServer, replaceDISCacheVersion} from 'coreUtils/utils';

import {DEFAULT_SEARCH_PARAMS, DATALAYER_CONFIGS, LOCALIZED_REFINEMENTS, REFINEMENT_DISALLOW_LIST} from 'brandConstants';
import {PERSONALIZATION_SEARCH_REFINEMENT} from 'brand/app/constants';

import SearchModel from '../../SearchModel';
import {ISearchParams, ISearchOptions, ESearchProvider, ESearchType} from '../../ISearch';
import AbstractSearchProvider from '../AbstractSearchProvider';
import {EXCLUDE_FROM_SEARCH_REFINEMENT, HIDE_IN_PLP_REFINEMENT} from './constants';

type EmitEventOptions = {
	searchResult: ProductSearchResultType;
	searchQuery: string;
	query: ProductSearchParams;
	eventSource: EEventSource;
}

/**
 * Represents SFCCSearchProvider
 */
class SFCCSearchProvider extends AbstractSearchProvider {
	/**
	 * Executes API call to get the searchModel based on given query
	 * @param {Object} query Array of product ids
	 * @returns Promise
	 */
	protected fetchSearch(query): Promise<ProductSearchResultType> {
		return this.managerRepository.getCommerceApi().shopperSearch.productSearch({parameters: query}) as Promise<ProductSearchResultType>;
	}

	/**
	 * Emit events for search results on the client side
	 * @param {EmitEventOptions} opts Options
	 * @returns void
	 */
	private emitEventsForSearchResults(opts: EmitEventOptions): void {
		if (isServer) {
			return;
		}

		const {searchResult, searchQuery, query, eventSource} = opts;
		const storedSearchModel = this.getStoredSearchModel();

		if (searchResult.total) {
			const productIds = searchResult.hits.map((productHit) => productHit.productId);
			this.managerRepository.getProductsMgr().getProductsSync(productIds).then((products) => {
				this.managerRepository.getEventsMgr().emit(EEventType.PRODUCT_LIST_LOAD, {
					source: eventSource,
					sourceSpecification: storedSearchModel.getSelectedRefinementById('cgid'),
					searchParams: query,
					searchQuery: searchQuery ? query.q : null,
					searchResult: searchResult,
					productsData: products.map((product, index) => {
						const productObject = {
							product,
							position: index + 1,
							plpCategoryForProduct: (DATALAYER_CONFIGS.plpCategoryForProduct && eventSource !== EEventSource.SEARCH_RESULTS)
								? storedSearchModel.getSelectedRefinementById('cgid')
								: '',
							attributes: undefined
						};

						if (DATALAYER_CONFIGS.addDimensions) {
							const priceModel = (product as ProductModel).getPriceModel();
							productObject.attributes = {
								dimension9: product.getSeason(),
								dimension10: priceModel.isSalesPriceBookApplied()
									? 'MD'
									: 'FP',
							};
						}

						return productObject;
					}),
				});
			});
		} else {
			// Send empty data to collect events for Reports & Dashboards: Searches Without Results
			this.managerRepository.getEventsMgr().emit(EEventType.PRODUCT_LIST_LOAD, {
				source: eventSource,
				sourceSpecification: storedSearchModel.getSelectedRefinementById('cgid'),
				searchParams: query,
				searchQuery: searchQuery ? query.q : null,
				searchResult: searchResult,
				productsData: []
			});
		}
	}

	/**
	 * Returns the SearchModel based on the given parameters
	 * If the SearchModel is not in store, it is fetched, stored and then returned
	 *
	 * @param {ISearchParams} params Search Params
	 * @param {ISearchOptions} options Search Options
	 * @returns {Promise<SearchModel>}
	 */
	public async search(params : ISearchParams = {}, options: ISearchOptions = {}): Promise<SearchModel> {
		const {
			refinements: requestedRefinements = this.userSelectedRefinements,
			sortingRule = this.userSelectedSorting,
			searchQuery = this.searchQuery,
			page = this.userSelectedPage,
			limitParam = this.limit,
			eventSource = EEventSource.SEARCH_RESULTS
		} = params;
		const {
			shouldEmitEvents = true,
		} = options;
		const customer = this.managerRepository.getCustomerMgr().getCurrentCustomer();
		const customerPersonalizationKey = customer.getCustomerPersonalizationKey();
		const configurationsMgr = this.managerRepository.getConfigurationsMgr();
		const enableHideInPLPFilter = configurationsMgr.get('sitePreferences.enableHideInPLPFilter');
		const enableExcludeFromSearch = configurationsMgr.get('sitePreferences.enableExcludeFromSearch');
		const isSearch = this.searchType === ESearchType.QUICK_SEARCH || Boolean(searchQuery);
		const refinements = {...requestedRefinements};

		// This refinement is added automatically only for an unfiltered category PLP
		if (enableHideInPLPFilter) {
			delete refinements[HIDE_IN_PLP_REFINEMENT];
		}

		// This refinement is added automatically only for search contexts (quick search / search results page)
		if (enableExcludeFromSearch) {
			delete refinements[EXCLUDE_FROM_SEARCH_REFINEMENT];
		}

		const query: ProductSearchParams = {
			q: '',
			refine: [],
			sort: '',
			offset: 0,
			limit: 0,
			allImages: false,
			perPricebook: true,
			allVariationProperties: true,
		};
		const wildCardParam = '*';

		this.setSearchQuery(searchQuery);

		/*
			We use HIDE_IN_PLP_REFINEMENT only in PLPs without filters
		 */
		const isPLPWithoutFilters = refinements.cgid?.length
			&& !Object.keys(refinements).some(r => !REFINEMENT_DISALLOW_LIST.includes(r) && refinements[r]?.length);

		if (isPLPWithoutFilters && enableHideInPLPFilter) {
			refinements[HIDE_IN_PLP_REFINEMENT] = ['false'];
		}

		if (isSearch && enableExcludeFromSearch) {
			refinements[EXCLUDE_FROM_SEARCH_REFINEMENT] = ['false'];
		}

		const includeProductSets = getConfig()?.app?.preferences?.searchIncludeProductSets ?? false;
		refinements.htype = includeProductSets ? ['variation_group', 'set'] : ['variation_group'];

		if (customerPersonalizationKey) {
			refinements[PERSONALIZATION_SEARCH_REFINEMENT] = [customerPersonalizationKey];
		}

		query.refine = Object.keys(refinements || {})
			.filter((key) => refinements[key]?.length)
			.map((key) => `${key}=${(refinements[key] as string[]).join('|')}`);
		query.sort = sortingRule || '';

		const limit = limitParam || DEFAULT_SEARCH_PARAMS.limit;
		this.setLimit(limit);
		query.limit = limit;
		query.offset = (page - 1) * limit;
		query.expand = 'availability,prices,images,custom_properties,promotions,variations,represented_products';

		if (searchQuery) {
			query.q = this.appendWildcardQuery ? searchQuery + wildCardParam : searchQuery;
		}

		const searchResult = await this.fetchSearch(query);

		this.storeSearch(searchResult, this.getKeyByQuery(query));

		shouldEmitEvents && this.emitEventsForSearchResults({
			searchResult,
			query,
			searchQuery,
			eventSource
		});

		return this.getStoredSearchModel();
	}

	/**
	 * Returns the suggestions based on the given parameters
	 *
	 * @returns {Promise<string[]>}
	 */
	public suggestions(): Promise<string[]> {
		return Promise.reject(new Error('Method not implemented.'));
	};

	/**
	 * Returns the suggestions based on the given parameters
	 *
	 * @param {ISearchSuggestionsParams} params Search Suggestions Params
	 * @returns {Promise<string[]>}
	 */
	public popularSearches(): Promise<string[]> {
		return Promise.reject(new Error('Method not implemented.'));
	};

	/**
	 * Translate refinements when change language
	 *
	 * @returns {Promise<void>}
	 */
	public translateRefinements() {
		// Not necessary to implement are already translated after search
		return Promise.resolve();
	}

	/**
	 * Translates Refinements
	 * @returns void
	 */
	async translateSelectedRefinements() {
		if (!this.userSelectedRefinementsToBeTranslated.size){
			return;
		}

		const selectedRefinements = this.getStoredSearchModel().getSelectedRefinements();

		if (!selectedRefinements) {
			return;
		}

		const selectedRefinementsKey: string[] = Object.keys(selectedRefinements)
			.filter((selectedRefinementKey) => LOCALIZED_REFINEMENTS.includes(selectedRefinementKey));

		if (!selectedRefinementsKey.length) {
			return;
		}

		const refinementModelValuesMap =
			await this.getRefinementsTranslationsMap(selectedRefinementsKey);

		const userSelectedRefinements = this.getUserSelectedRefinements();

		for (const selectedRefinementKey of selectedRefinementsKey) {
			const selectedRefinementValues: string[] = selectedRefinements[selectedRefinementKey].split('|');

			if (!selectedRefinementValues.length) {
				continue;
			}

			for (const selectedRefinementValue of selectedRefinementValues) {
				if (!this.userSelectedRefinementsToBeTranslated.has(selectedRefinementValue)) {
					continue;
				}

				const presentationId: string =
					this.userSelectedRefinementsToBeTranslated.get(selectedRefinementValue);

				if (!refinementModelValuesMap.has(presentationId)) {
					continue;
				}

				const valueIndex: number = userSelectedRefinements[selectedRefinementKey]
					.findIndex((userSelectedRefinement) =>
						userSelectedRefinement === selectedRefinementValue);

				userSelectedRefinements[selectedRefinementKey][valueIndex] =
					refinementModelValuesMap.get(presentationId);
			}
		}

		this.setUserSelectedRefinements(userSelectedRefinements);
	}

	/**
	 * Returns refinement models in a particular language
	 *
	 * @param {string} languageCode language code
	 * @param {string | undefined} cgid category id
	 * @param {string | undefined} searchQuery category id
	 * @returns object
	 */
	async getRefinementsAndSortingByLanguage(languageCode: string, cgid: string | undefined, searchQuery: string | undefined) {
		const searchParam = this.getSearchParamTranslation(cgid, searchQuery);

		searchParam.locale = languageCode;

		const searchTranslatedRefinementsAndSorting = await this.fetchSearch(searchParam);

		return searchTranslatedRefinementsAndSorting;
	}

	/**
	 * Translate refinements and url when
	 * redirect in a different language triggers
	 *
	 * @param {string | undefined} cgid category id
	 * @param {object} refinements current search refinements
	 * @param {string | undefined} searchQuery search query
	 * @param {string} languageCodeFromUrl language code in the url
	 * @param {string} currentLanguageCode current language code
	 *
	 * @returns Promise<void>
	 */
	async handleSearchRedirectInAnotherLang(
		cgid: string | undefined,
		refinements: object,
		searchQuery: string | undefined,
		languageCodeFromUrl: string,
		currentLanguageCode: string
	): Promise<void> {
		const refinementsAndSortingByUrlLanguage = await this.getRefinementsAndSortingByLanguage(languageCodeFromUrl, cgid, searchQuery);
		const refinementsAndSortingByUrlLanguageMap = new Map();

		refinementsAndSortingByUrlLanguage
			.refinements?.forEach((ref) => {
				let values = new Map();
				ref.values?.forEach((valueObj) => {
					values.set(valueObj.value, valueObj);
				});
				refinementsAndSortingByUrlLanguageMap.set(ref.attributeId, values);
			});

		const refinementsAndSortingByCurrentLanguage =
			await this.getRefinementsAndSortingByLanguage(currentLanguageCode, cgid, searchQuery);
		const refinementsAndSortingByCurrentLanguageMap = new Map();

		refinementsAndSortingByCurrentLanguage
			.refinements?.forEach((ref) => {
				let values = new Map();
				ref.values?.forEach((valueObj) => {
					values.set(valueObj.presentationId, valueObj);
				});
				refinementsAndSortingByCurrentLanguageMap.set(ref.attributeId, values);
			});

		const refinementKeys = Object.keys(refinements);

		refinementKeys?.forEach((refinementKey) => {
			if (LOCALIZED_REFINEMENTS.includes(refinementKey)) {
				const refinementValues = refinements[refinementKey].flatMap((val: string) => val.split(',').map(s => s.trim()));
				const urlTranslatedRefinement = refinementsAndSortingByUrlLanguageMap.get(refinementKey);
				const currentTranslatedRefinement = refinementsAndSortingByCurrentLanguageMap.get(refinementKey);

				refinementValues?.forEach((refinementValue, index) => {
					const completeRefinementDataFromUrl = urlTranslatedRefinement.get(refinementValue);
					const refinementData = currentTranslatedRefinement.get(completeRefinementDataFromUrl?.presentationId);

					refinements[refinementKey][index] = refinementData.value;
				});
			}
		});
	}

	/**
	 * Stores the given search result.
	 * @param {ProductSearchResultType} productSearchResult The response to store
	 * @param {string} queryKey The queryKey to be used in the store. This is needed to take a fresh value if the refinements or the searchQuery changes
	 * @protected
	 * @returns void
	 */
	protected storeSearch(productSearchResult: ProductSearchResultType, queryKey: string) {
		const DIS_STATIC_PATH = this.managerRepository.getEnvPreferencesMgr().get('DIS_STATIC_PATH');

		if (DIS_STATIC_PATH && productSearchResult.hits) {
			replaceDISCacheVersion(productSearchResult.hits, DIS_STATIC_PATH);
		}

		return super.storeSearch(productSearchResult, queryKey);
	}

	/**
	 * Returns the type of the search provider.
	 *
	 * @returns {ESearchProvider} The enum value representing the SFCC search provider.
	 */
	getProviderType(): ESearchProvider {
		return ESearchProvider.SFCC;
	}
}

export default SFCCSearchProvider;
